import { formatCourseClock, formatCourseDateParts } from '@/lib/courseDateFormat';

export const HIGHLIGHT_COLORS = ['yellow', 'green', 'blue', 'pink'] as const;
export type HighlightColor = (typeof HIGHLIGHT_COLORS)[number];

export const HIGHLIGHT_VISIBILITIES = ['private', 'clinic'] as const;
export type HighlightVisibility = (typeof HIGHLIGHT_VISIBILITIES)[number];

export const HIGHLIGHT_FILTERS = ['all', 'mine', 'shared'] as const;
export type HighlightFilter = (typeof HIGHLIGHT_FILTERS)[number];

export const HIGHLIGHT_ZOOM_PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3] as const;
export const HIGHLIGHT_ZOOM_MIN = 0.5;
export const HIGHLIGHT_ZOOM_MAX = 3;
/** Ignore fit-width jitter smaller than this so a scrollbar cannot retrigger a blank render. */
export const HIGHLIGHT_ZOOM_EPSILON = 0.02;
export const HIGHLIGHT_SEARCH_MIN = 10;
export const HIGHLIGHT_RAIL_MIN_GAP = 26;
export const HIGHLIGHT_COMMENT_MAX = 500;
export const PDF_CSS_DPI = 96 / 72;
/** Horizontal padding on `.fp-pages` (must match design-v2.css). */
export const FILE_PREVIEW_PAGE_SIDE_MARGIN = 40;

export interface HighlightRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Character offsets into the concatenated text nodes of one preview page.
 * PDF highlights keep using `rects`. HTML (Word) highlights use this anchor
 * because a mammoth document has no PDF page box. Stored as an extra object
 * in the existing `rects` JSON array so old rows stay valid without a migration.
 */
export interface HighlightTextAnchor {
  start: number;
  end: number;
}

export const HIGHLIGHT_TEXT_ANCHOR_KIND = 'text' as const;

/**
 * Excel preview cells. `range` is A1 notation on the preview grid
 * (`B3` or `B3:D5`). `start`/`end` are character offsets inside the single
 * cell when only part of its text is selected. Stored in the same `rects`
 * JSON array as PDF rectangles and Word text anchors.
 */
export interface HighlightCellAnchor {
  sheet: string;
  range: string;
  start?: number;
  end?: number;
}

export const HIGHLIGHT_CELL_ANCHOR_KIND = 'cell' as const;

const CELL_REF_PATTERN = /^([A-Z]+)(\d+)$/;
const CELL_RANGE_PATTERN = /^[A-Z]+\d+(?::[A-Z]+\d+)?$/;

export function columnLetters(column: number): string {
  let n = column + 1;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

export function cellRef(column: number, row: number): string {
  return `${columnLetters(column)}${row}`;
}

export function parseCellRef(ref: string): { column: number; row: number } | null {
  const match = CELL_REF_PATTERN.exec(ref);
  if (!match) return null;
  let column = 0;
  for (const char of match[1] ?? '') column = column * 26 + (char.charCodeAt(0) - 64);
  column -= 1;
  const row = Number(match[2]);
  if (!Number.isInteger(row) || row < 1 || column < 0) return null;
  return { column, row };
}

export function formatCellRange(
  start: { column: number; row: number },
  end: { column: number; row: number },
): string {
  const left = cellRef(Math.min(start.column, end.column), Math.min(start.row, end.row));
  const right = cellRef(Math.max(start.column, end.column), Math.max(start.row, end.row));
  return left === right ? left : `${left}:${right}`;
}

export function expandCellRange(range: string): string[] {
  if (!CELL_RANGE_PATTERN.test(range)) return [];
  const [left, right] = range.split(':');
  const start = parseCellRef(left ?? '');
  const end = parseCellRef(right ?? left ?? '');
  if (!start || !end) return [];
  const refs: string[] = [];
  for (let row = Math.min(start.row, end.row); row <= Math.max(start.row, end.row); row += 1) {
    for (
      let column = Math.min(start.column, end.column);
      column <= Math.max(start.column, end.column);
      column += 1
    ) {
      refs.push(cellRef(column, row));
    }
  }
  return refs;
}

export interface FileHighlightDraft {
  page: number;
  quote: string;
  prefix: string;
  suffix: string;
  rects: HighlightRect[];
  textAnchor: HighlightTextAnchor | null;
  cellAnchor: HighlightCellAnchor | null;
  clientX: number;
  clientY: number;
}

export interface FileHighlight {
  id: string;
  clinic_id: string;
  document_id: string;
  storage_path: string;
  page: number;
  quote: string;
  prefix: string;
  suffix: string;
  rects: HighlightRect[];
  textAnchor: HighlightTextAnchor | null;
  cellAnchor: HighlightCellAnchor | null;
  color: HighlightColor;
  comment: string | null;
  visibility: HighlightVisibility;
  created_by: string;
  created_at: string;
  updated_at: string;
  author_name: string | null;
  /** Markerings tags linked to this highlight, when the link table could be read. */
  tagIds?: string[];
}

export interface NumberedHighlight extends FileHighlight {
  number: number;
}

export type HighlightSelectionAnalysis =
  | { type: 'none' }
  | { type: 'multi-page' }
  | { type: 'draft'; draft: FileHighlightDraft };

const CONTEXT_CHARS = 40;
const COPENHAGEN_WEEKDAY: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function isHighlightColor(value: string): value is HighlightColor {
  return (HIGHLIGHT_COLORS as readonly string[]).includes(value);
}

export function isHighlightVisibility(value: string): value is HighlightVisibility {
  return (HIGHLIGHT_VISIBILITIES as readonly string[]).includes(value);
}

export function highlightColorCssVar(color: HighlightColor): string {
  switch (color) {
    case 'yellow':
      return 'var(--fp-hl-yellow)';
    case 'green':
      return 'var(--fp-hl-green)';
    case 'blue':
      return 'var(--fp-hl-blue)';
    case 'pink':
      return 'var(--fp-hl-pink)';
    default: {
      const _never: never = color;
      return _never;
    }
  }
}

export function highlightColorKey(color: HighlightColor): string {
  switch (color) {
    case 'yellow':
      return 'documents.highlights.color_yellow';
    case 'green':
      return 'documents.highlights.color_green';
    case 'blue':
      return 'documents.highlights.color_blue';
    case 'pink':
      return 'documents.highlights.color_pink';
    default: {
      const _never: never = color;
      return _never;
    }
  }
}

/** PDF export inks. Same hex as `--fp-hl-*` in design-v2.css. */
export function highlightColorRgb(color: HighlightColor): { r: number; g: number; b: number } {
  switch (color) {
    case 'yellow':
      return { r: 1, g: 241 / 255, b: 118 / 255 };
    case 'green':
      return { r: 125 / 255, g: 232 / 255, b: 168 / 255 };
    case 'blue':
      return { r: 142 / 255, g: 197 / 255, b: 1 };
    case 'pink':
      return { r: 1, g: 158 / 255, b: 194 / 255 };
    default: {
      const _never: never = color;
      return _never;
    }
  }
}

export function parseHighlightRects(value: unknown): HighlightRect[] {
  if (!Array.isArray(value)) return [];
  const rects: HighlightRect[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const x = Number(rec.x);
    const y = Number(rec.y);
    const w = Number(rec.w);
    const h = Number(rec.h);
    if (![x, y, w, h].every((n) => Number.isFinite(n))) continue;
    rects.push({
      x: clamp01(x),
      y: clamp01(y),
      w: clamp01(w),
      h: clamp01(h),
    });
  }
  return rects;
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

export function normalizeHighlightText(value: string): string {
  return value.replace(/\s+/gu, ' ').trim();
}

export function parseHighlightTextAnchor(value: unknown): HighlightTextAnchor | null {
  if (!Array.isArray(value)) return null;
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    if (rec.kind !== HIGHLIGHT_TEXT_ANCHOR_KIND) continue;
    const start = Number(rec.start);
    const end = Number(rec.end);
    if (!Number.isInteger(start) || !Number.isInteger(end)) continue;
    if (start < 0 || end <= start || end > 1_000_000) continue;
    return { start, end };
  }
  return null;
}

export function parseHighlightCellAnchor(value: unknown): HighlightCellAnchor | null {
  if (!Array.isArray(value)) return null;
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    if (rec.kind !== HIGHLIGHT_CELL_ANCHOR_KIND) continue;
    const sheet = typeof rec.sheet === 'string' ? rec.sheet.slice(0, 200) : '';
    const range = typeof rec.range === 'string' ? rec.range.toUpperCase() : '';
    if (!sheet || !CELL_RANGE_PATTERN.test(range) || expandCellRange(range).length === 0) continue;
    const start = rec.start === undefined ? undefined : Number(rec.start);
    const end = rec.end === undefined ? undefined : Number(rec.end);
    if (
      start !== undefined &&
      end !== undefined &&
      Number.isInteger(start) &&
      Number.isInteger(end) &&
      start >= 0 &&
      end > start &&
      end <= 8000
    ) {
      return { sheet, range, start, end };
    }
    return { sheet, range };
  }
  return null;
}

export function highlightGeometryJson(
  rects: HighlightRect[],
  textAnchor: HighlightTextAnchor | null,
  cellAnchor: HighlightCellAnchor | null = null,
): Array<
  | HighlightRect
  | { kind: typeof HIGHLIGHT_TEXT_ANCHOR_KIND; start: number; end: number }
  | {
      kind: typeof HIGHLIGHT_CELL_ANCHOR_KIND;
      sheet: string;
      range: string;
      start?: number;
      end?: number;
    }
> {
  const rows: Array<
    | HighlightRect
    | { kind: typeof HIGHLIGHT_TEXT_ANCHOR_KIND; start: number; end: number }
    | {
        kind: typeof HIGHLIGHT_CELL_ANCHOR_KIND;
        sheet: string;
        range: string;
        start?: number;
        end?: number;
      }
  > = rects.map((rect) => ({
    x: rect.x,
    y: rect.y,
    w: rect.w,
    h: rect.h,
  }));
  if (textAnchor) {
    rows.push({ kind: HIGHLIGHT_TEXT_ANCHOR_KIND, start: textAnchor.start, end: textAnchor.end });
  }
  if (cellAnchor) {
    const cell: {
      kind: typeof HIGHLIGHT_CELL_ANCHOR_KIND;
      sheet: string;
      range: string;
      start?: number;
      end?: number;
    } = {
      kind: HIGHLIGHT_CELL_ANCHOR_KIND,
      sheet: cellAnchor.sheet,
      range: cellAnchor.range,
    };
    if (cellAnchor.start !== undefined && cellAnchor.end !== undefined) {
      cell.start = cellAnchor.start;
      cell.end = cellAnchor.end;
    }
    rows.push(cell);
  }
  return rows;
}

export function rangeFromTextOffsets(root: HTMLElement, start: number, end: number): Range | null {
  if (end <= start || start < 0) return null;
  const startPoint = textPointAt(root, start);
  const endPoint = textPointAt(root, end);
  if (!startPoint || !endPoint) return null;
  const range = root.ownerDocument.createRange();
  range.setStart(startPoint.node, startPoint.offset);
  range.setEnd(endPoint.node, endPoint.offset);
  return range.collapsed ? null : range;
}

function textPointAt(root: HTMLElement, offset: number): { node: Text; offset: number } | null {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let node = walker.nextNode();
  while (node) {
    const text = node as Text;
    if (remaining <= text.data.length) return { node: text, offset: remaining };
    remaining -= text.data.length;
    node = walker.nextNode();
  }
  return null;
}

export function textAnchorFromRange(root: HTMLElement, range: Range): HighlightTextAnchor | null {
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const start = boundaryOffset(root, range.startContainer, range.startOffset);
  const end = boundaryOffset(root, range.endContainer, range.endOffset);
  if (start == null || end == null || end <= start) return null;
  return { start, end };
}

function boundaryOffset(root: Node, container: Node, offset: number): number | null {
  if (container.nodeType === Node.TEXT_NODE) return offsetOfText(root, container, offset);
  if (container.nodeType !== Node.ELEMENT_NODE) return null;
  const child = container.childNodes[offset] ?? null;
  if (child) return offsetBefore(root, child);
  if (offset === 0) return offsetBefore(root, container);
  const previous = container.childNodes[offset - 1];
  if (!previous) return null;
  return offsetAfter(root, previous);
}

function offsetOfText(root: Node, text: Node, offset: number): number | null {
  let count = 0;
  const walker = text.ownerDocument?.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  if (!walker) return null;
  let current = walker.nextNode();
  while (current) {
    if (current === text) {
      const length = current.textContent?.length ?? 0;
      return count + Math.min(Math.max(0, offset), length);
    }
    count += current.textContent?.length ?? 0;
    current = walker.nextNode();
  }
  return null;
}

function offsetBefore(root: Node, node: Node): number | null {
  let count = 0;
  const walker = node.ownerDocument?.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  if (!walker) return null;
  let current = walker.nextNode();
  while (current) {
    if (node === current || node.contains(current)) return count;
    const position = node.compareDocumentPosition(current);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return count;
    count += current.textContent?.length ?? 0;
    current = walker.nextNode();
  }
  return root.contains(node) ? count : null;
}

function offsetAfter(root: Node, node: Node): number | null {
  let count = 0;
  const walker = node.ownerDocument?.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  if (!walker) return null;
  let current = walker.nextNode();
  let seen = false;
  while (current) {
    const inside = node === current || node.contains(current);
    if (inside) seen = true;
    else if (seen) return count;
    count += current.textContent?.length ?? 0;
    current = walker.nextNode();
  }
  return seen || root.contains(node) ? count : null;
}

export function documentPreviewRenditionPath(storagePath: string, documentId: string): string {
  const parts = storagePath.split('/');
  parts[parts.length - 1] = `${documentId}.preview.pdf`;
  return parts.join('/');
}

export function formatHighlightTime(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Copenhagen',
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  const weekday = COPENHAGEN_WEEKDAY[value('weekday')] ?? 0;
  return `${formatCourseDateParts(
    {
      date,
      weekday,
      day: Number(value('day')),
      month: Number(value('month')) - 1,
      year: Number(value('year')),
      hours: Number(value('hour')),
      minutes: Number(value('minute')),
    },
    locale,
  )} · ${formatCourseClock(Number(value('hour')), Number(value('minute')))}`;
}

function pageElementFromNode(node: Node | null): HTMLElement | null {
  const el = node instanceof Element ? node : node?.parentElement;
  return el?.closest('[data-fp-page]') ?? null;
}

export function selectionToHighlightDraft(
  selection: Selection | null,
  host: HTMLElement,
): FileHighlightDraft | null {
  const analysis = analyzeHighlightSelection(selection, host);
  return analysis.type === 'draft' ? analysis.draft : null;
}

function sheetCellAnchor(page: HTMLElement, range: Range): HighlightCellAnchor | null {
  const positions: { cell: HTMLElement; column: number; row: number }[] = [];
  for (const cell of page.querySelectorAll<HTMLElement>('td[data-fp-row][data-fp-col]')) {
    let hits = false;
    try {
      hits = range.intersectsNode(cell);
    } catch {
      hits = false;
    }
    if (!hits) continue;
    const column = Number(cell.dataset.fpCol);
    const row = Number(cell.dataset.fpRow);
    if (!Number.isInteger(column) || !Number.isInteger(row) || column < 0 || row < 1) continue;
    positions.push({ cell, column, row });
  }
  const first = positions[0];
  if (!first) return null;
  let minColumn = first.column;
  let maxColumn = first.column;
  let minRow = first.row;
  let maxRow = first.row;
  for (const item of positions) {
    minColumn = Math.min(minColumn, item.column);
    maxColumn = Math.max(maxColumn, item.column);
    minRow = Math.min(minRow, item.row);
    maxRow = Math.max(maxRow, item.row);
  }
  const anchor: HighlightCellAnchor = {
    sheet: page.dataset.fpSheet?.slice(0, 200) || 'Ark',
    range: formatCellRange({ column: minColumn, row: minRow }, { column: maxColumn, row: maxRow }),
  };
  if (positions.length === 1) {
    const text = textAnchorFromRange(first.cell, range);
    const full = first.cell.textContent ?? '';
    if (text && (text.start > 0 || text.end < full.length)) {
      anchor.start = text.start;
      anchor.end = text.end;
    }
  }
  return anchor;
}

export function analyzeHighlightSelection(
  selection: Selection | null,
  host: HTMLElement,
): HighlightSelectionAnalysis {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return { type: 'none' };
  const range = selection.getRangeAt(0);
  if (!host.contains(range.commonAncestorContainer)) return { type: 'none' };

  const startPage = pageElementFromNode(range.startContainer);
  const endPage = pageElementFromNode(range.endContainer);
  if (!startPage || !endPage || !host.contains(startPage) || !host.contains(endPage)) {
    return { type: 'none' };
  }
  if (startPage !== endPage) return { type: 'multi-page' };

  const page = Number(startPage.dataset.fpPage);
  if (!Number.isInteger(page) || page < 1) return { type: 'none' };

  const quote = normalizeHighlightText(range.toString());
  if (!quote) return { type: 'none' };

  const html = startPage.dataset.fpText === 'html';
  const sheet = startPage.dataset.fpText === 'sheet';
  const textAnchor = sheet ? null : textAnchorFromRange(startPage, range);
  const cellAnchor = sheet ? sheetCellAnchor(startPage, range) : null;
  const pageRect = startPage.getBoundingClientRect();
  const rects =
    html || pageRect.width < 1 || pageRect.height < 1
      ? []
      : Array.from(range.getClientRects())
          .filter((rect) => rect.width > 0 && rect.height > 0)
          .map((rect) => ({
            x: clamp01((rect.left - pageRect.left) / pageRect.width),
            y: clamp01((rect.top - pageRect.top) / pageRect.height),
            w: clamp01(rect.width / pageRect.width),
            h: clamp01(rect.height / pageRect.height),
          }))
          .filter((rect) => rect.w > 0 && rect.h > 0);

  if (sheet) {
    if (!cellAnchor) return { type: 'none' };
  } else if (html) {
    if (!textAnchor) return { type: 'none' };
  } else if (rects.length === 0) {
    return { type: 'none' };
  }

  const pageText = normalizeHighlightText(startPage.innerText || startPage.textContent || '');
  const idx = pageText.indexOf(quote);
  const prefix = idx >= 0 ? pageText.slice(Math.max(0, idx - CONTEXT_CHARS), idx) : '';
  const suffix =
    idx >= 0 ? pageText.slice(idx + quote.length, idx + quote.length + CONTEXT_CHARS) : '';

  const first =
    typeof range.getBoundingClientRect === 'function'
      ? range.getBoundingClientRect()
      : { left: 0, top: 0, width: 0, height: 0 };
  return {
    type: 'draft',
    draft: {
      page,
      quote: quote.slice(0, 8000),
      prefix: prefix.slice(0, 200),
      suffix: suffix.slice(0, 200),
      rects: sheet ? [] : rects,
      textAnchor,
      cellAnchor,
      clientX: first.left + first.width / 2,
      clientY: first.top,
    },
  };
}

export function isInsideHighlightPanel(target: EventTarget | Node | null): boolean {
  if (target instanceof Element) return Boolean(target.closest('[data-fp-panel]'));
  if (target instanceof Node) return Boolean(target.parentElement?.closest('[data-fp-panel]'));
  return false;
}

/**
 * Read the selection after the pointer is released.
 *
 * Safari/WebKit updates `getSelection()` after `mouseup`. Reading it in that
 * handler, or in a `setTimeout(0)`, is often still empty, so nothing is
 * selected on a Mac trackpad. `pointerup` plus two animation frames matches
 * when WebKit commits the range. `selectionchange` covers the same moment
 * when it arrives after the pointer is up. Events while the button is down
 * are ignored so Chrome does not re-render the toolbar on every dragged
 * character (that flicker was the "works, but badly" report).
 */
export function bindHighlightSelection(
  host: HTMLElement,
  onTextSelected: (analysis: HighlightSelectionAnalysis) => void,
): () => void {
  let pointerDownInHost = false;
  let frame = 0;
  let framesLeft = 0;

  const read = () => {
    frame = 0;
    if (isInsideFilePreviewChrome(document.activeElement)) return;
    onTextSelected(analyzeHighlightSelection(window.getSelection(), host));
  };

  const scheduleRead = () => {
    if (frame) cancelAnimationFrame(frame);
    framesLeft = 2;
    const step = () => {
      framesLeft -= 1;
      if (framesLeft > 0) {
        frame = requestAnimationFrame(step);
        return;
      }
      read();
    };
    frame = requestAnimationFrame(step);
  };

  const onPointerDown = (event: Event) => {
    if (!(event.target instanceof Node) || !host.contains(event.target)) return;
    if (isInsideFilePreviewChrome(event.target)) return;
    pointerDownInHost = true;
  };

  const onPointerUp = (event: Event) => {
    if (!pointerDownInHost) return;
    pointerDownInHost = false;
    if (
      isInsideFilePreviewChrome(event.target) &&
      (!(event.target instanceof Node) || !host.contains(event.target))
    ) {
      return;
    }
    scheduleRead();
  };

  const onSelectionChange = () => {
    if (pointerDownInHost) return;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!host.contains(range.commonAncestorContainer)) return;
    scheduleRead();
  };

  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointerup', onPointerUp, true);
  document.addEventListener('selectionchange', onSelectionChange);
  return () => {
    if (frame) cancelAnimationFrame(frame);
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('pointerup', onPointerUp, true);
    document.removeEventListener('selectionchange', onSelectionChange);
  };
}

export function highlightIdAtClientPoint(
  page: HTMLElement,
  clientX: number,
  clientY: number,
  highlights: { id: string; rects: HighlightRect[] }[],
): string | null {
  const box = page.getBoundingClientRect();
  if (box.width < 1 || box.height < 1) return null;
  const x = (clientX - box.left) / box.width;
  const y = (clientY - box.top) / box.height;
  for (let index = highlights.length - 1; index >= 0; index -= 1) {
    const highlight = highlights[index];
    if (!highlight) continue;
    for (const rect of highlight.rects) {
      if (x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h) {
        return highlight.id;
      }
    }
  }
  return null;
}

export function isInsideFilePreviewChrome(target: EventTarget | Node | null): boolean {
  if (target instanceof Element) {
    return Boolean(target.closest('[data-fp-chrome], [data-fp-panel], [data-fp-menu]'));
  }
  if (target instanceof Node) {
    return Boolean(
      target.parentElement?.closest('[data-fp-chrome], [data-fp-panel], [data-fp-menu]'),
    );
  }
  return false;
}

export function canEditHighlight(input: {
  highlight: Pick<FileHighlight, 'created_by'>;
  userId: string | null | undefined;
}): boolean {
  if (!input.userId) return false;
  return input.highlight.created_by === input.userId;
}

export function canDeleteHighlight(input: {
  highlight: Pick<FileHighlight, 'created_by' | 'visibility'>;
  userId: string | null | undefined;
  isClinicAdmin: boolean;
}): boolean {
  if (!input.userId) return false;
  if (input.highlight.created_by === input.userId) return true;
  return input.highlight.visibility === 'clinic' && input.isClinicAdmin;
}

export function firstHighlightRect(highlight: Pick<FileHighlight, 'rects'>): HighlightRect | null {
  return highlight.rects[0] ?? null;
}

export function numberHighlights(highlights: FileHighlight[]): NumberedHighlight[] {
  const sorted = [...highlights].sort((a, b) => compareHighlightDocumentOrder(a, b));
  return sorted.map((highlight, index) => ({ ...highlight, number: index + 1 }));
}

export function compareHighlightDocumentOrder(
  a: Pick<FileHighlight, 'page' | 'rects' | 'created_at' | 'id'> & {
    textAnchor?: HighlightTextAnchor | null;
    cellAnchor?: HighlightCellAnchor | null;
  },
  b: Pick<FileHighlight, 'page' | 'rects' | 'created_at' | 'id'> & {
    textAnchor?: HighlightTextAnchor | null;
    cellAnchor?: HighlightCellAnchor | null;
  },
): number {
  if (a.page !== b.page) return a.page - b.page;
  const ar = firstHighlightRect(a);
  const br = firstHighlightRect(b);
  if (ar || br) {
    const ay = ar?.y ?? 0;
    const by = br?.y ?? 0;
    if (ay !== by) return ay - by;
    const ax = ar?.x ?? 0;
    const bx = br?.x ?? 0;
    if (ax !== bx) return ax - bx;
  } else {
    const ao = flowAnchorOrder(a);
    const bo = flowAnchorOrder(b);
    if (ao !== bo) return ao - bo;
  }
  if (a.created_at !== b.created_at) return a.created_at.localeCompare(b.created_at);
  return a.id.localeCompare(b.id);
}

/** Reading order for text and spreadsheet marks that have no PDF rectangle. */
function flowAnchorOrder(highlight: {
  textAnchor?: HighlightTextAnchor | null;
  cellAnchor?: HighlightCellAnchor | null;
}): number {
  if (highlight.textAnchor) return highlight.textAnchor.start;
  const cell = highlight.cellAnchor;
  if (!cell) return Number.MAX_SAFE_INTEGER;
  const parsed = parseCellRef(cell.range.split(':')[0] ?? '');
  if (!parsed) return Number.MAX_SAFE_INTEGER;
  return parsed.row * 1000 + parsed.column;
}

export function matchesHighlightFilter(
  highlight: FileHighlight,
  filter: HighlightFilter,
  userId: string | null | undefined,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'mine':
      return Boolean(userId) && highlight.created_by === userId;
    case 'shared':
      return highlight.visibility === 'clinic';
    default: {
      const _never: never = filter;
      return _never;
    }
  }
}

export function matchesHighlightQuery(highlight: FileHighlight, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase('da');
  if (!needle) return true;
  const hay = [highlight.quote, highlight.comment ?? '', highlight.author_name ?? '']
    .join('\n')
    .toLocaleLowerCase('da');
  return hay.includes(needle);
}

export function filterNumberedHighlights(
  highlights: NumberedHighlight[],
  input: { filter: HighlightFilter; query: string; userId: string | null | undefined },
): NumberedHighlight[] {
  return highlights.filter(
    (highlight) =>
      matchesHighlightFilter(highlight, input.filter, input.userId) &&
      matchesHighlightQuery(highlight, input.query),
  );
}

export function highlightFilterCounts(
  highlights: FileHighlight[],
  userId: string | null | undefined,
): Record<HighlightFilter, number> {
  return {
    all: highlights.length,
    mine: highlights.filter((row) => matchesHighlightFilter(row, 'mine', userId)).length,
    shared: highlights.filter((row) => matchesHighlightFilter(row, 'shared', userId)).length,
  };
}

export function groupHighlightsByPage(highlights: NumberedHighlight[]): {
  page: number;
  items: NumberedHighlight[];
}[] {
  const map = new Map<number, NumberedHighlight[]>();
  for (const highlight of highlights) {
    const list = map.get(highlight.page) ?? [];
    list.push(highlight);
    map.set(highlight.page, list);
  }
  return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([page, items]) => ({ page, items }));
}

export function clampHighlightZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 1;
  return Math.min(HIGHLIGHT_ZOOM_MAX, Math.max(HIGHLIGHT_ZOOM_MIN, zoom));
}

export function sameHighlightZoom(a: number, b: number): boolean {
  return Math.abs(a - b) < HIGHLIGHT_ZOOM_EPSILON;
}

export function pageSideMarginPx(host: HTMLElement | null): number {
  const pages = host?.querySelector('.fp-pages');
  if (pages instanceof HTMLElement) {
    const left = Number.parseFloat(getComputedStyle(pages).paddingLeft);
    if (Number.isFinite(left) && left > 0) return left;
  }
  return FILE_PREVIEW_PAGE_SIDE_MARGIN;
}

export function fitHighlightZoom(
  documentAreaWidth: number,
  pageWidth100: number,
  sideMargin = FILE_PREVIEW_PAGE_SIDE_MARGIN,
): number {
  const usable = documentAreaWidth - 2 * sideMargin;
  if (!(usable > 0) || !(pageWidth100 > 0)) return clampHighlightZoom(1);
  return clampHighlightZoom(usable / pageWidth100);
}

export function highlightsVisibleStorageKey(userId: string): string {
  return `clinio-file-highlights-visible:${userId}`;
}

export function readHighlightsVisible(userId: string | null | undefined): boolean {
  if (!userId || typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(highlightsVisibleStorageKey(userId));
    if (raw === '0') return false;
    if (raw === '1') return true;
    return true;
  } catch {
    return true;
  }
}

export function writeHighlightsVisible(userId: string | null | undefined, visible: boolean): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(highlightsVisibleStorageKey(userId), visible ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
}

/** Markering column for this user. Same idea as Værktøjer: open until they close it. */
export function markingPanelStorageKey(userId: string): string {
  return `clinio-file-marking-panel:${userId}`;
}

export function readMarkingPanelOpen(userId: string | null | undefined): boolean {
  if (!userId || typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(markingPanelStorageKey(userId));
    if (raw === '0') return false;
    if (raw === '1') return true;
    return true;
  } catch {
    return true;
  }
}

export function writeMarkingPanelOpen(userId: string | null | undefined, open: boolean): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(markingPanelStorageKey(userId), open ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
}

/** Filer column in the file view. Open until this user closes it, independent of Markering. */
export function filesPanelStorageKey(userId: string): string {
  return `clinio-file-browser-panel:${userId}`;
}

export function readFilesPanelOpen(userId: string | null | undefined): boolean {
  if (!userId || typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(filesPanelStorageKey(userId));
    if (raw === '0') return false;
    if (raw === '1') return true;
    return true;
  } catch {
    return true;
  }
}

export function writeFilesPanelOpen(userId: string | null | undefined, open: boolean): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(filesPanelStorageKey(userId), open ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Filer and Markering column widths in the file view.
 * Journal only slides Tidligere notater. Here both side columns slide, so
 * either files or comments can take more of the window. The document keeps
 * a minimum, and one column never takes more than half the window.
 */
export const FILE_SIDE_WIDTH_MIN = 280;
export const FILE_SIDE_WIDTH_DEFAULT = 360;
export const FILE_SIDE_WIDTH_STEP = 16;
/**
 * Horizontal space a closed Filer or Markering rail takes.
 * 24px screen edge + 48px journal rail card + 16px gap. Keep in step with
 * `--fp-rail` in design-v2.css.
 */
export const FILE_PREVIEW_RAIL_WIDTH = 24 + 48 + 16;
export const FILE_PREVIEW_DOCUMENT_MIN = 320;

export type FileSideColumn = 'files' | 'marking';
export type FileSidePresence = 'open' | 'rail' | 'hidden';

export function fileSideWidthStorageKey(userId: string, side: FileSideColumn): string {
  return side === 'files'
    ? `clinio-file-browser-width:${userId}`
    : `clinio-file-marking-width:${userId}`;
}

export function fileSideOccupied(presence: FileSidePresence, width: number): number {
  if (presence === 'hidden') return 0;
  if (presence === 'rail') return FILE_PREVIEW_RAIL_WIDTH;
  return width;
}

/** Largest width this column may use while the other column and the document still fit. */
export function maxFileSideWidth(viewportWidth: number, otherOccupied: number): number {
  const half = Math.floor(viewportWidth * 0.5);
  const room = Math.floor(viewportWidth - otherOccupied - FILE_PREVIEW_DOCUMENT_MIN);
  return Math.max(FILE_SIDE_WIDTH_DEFAULT, Math.min(half, room));
}

export function clampFileSideWidth(
  width: number,
  viewportWidth: number,
  otherOccupied: number,
): number {
  const max = maxFileSideWidth(viewportWidth, otherOccupied);
  return Math.min(max, Math.max(FILE_SIDE_WIDTH_MIN, Math.round(width)));
}

export function readFileSideWidth(userId: string | null | undefined, side: FileSideColumn): number {
  if (!userId || typeof window === 'undefined') return FILE_SIDE_WIDTH_DEFAULT;
  try {
    const raw = window.localStorage.getItem(fileSideWidthStorageKey(userId, side));
    const parsed = raw == null ? NaN : Number(raw);
    if (!Number.isFinite(parsed)) return FILE_SIDE_WIDTH_DEFAULT;
    return Math.max(FILE_SIDE_WIDTH_MIN, Math.round(parsed));
  } catch {
    return FILE_SIDE_WIDTH_DEFAULT;
  }
}

export function writeFileSideWidth(
  userId: string | null | undefined,
  side: FileSideColumn,
  width: number,
): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      fileSideWidthStorageKey(userId, side),
      String(Math.max(FILE_SIDE_WIDTH_MIN, Math.round(width))),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Widths actually painted. A saved preference can be wider than the current
 * window allows; the other preference is kept so it comes back when there is room.
 */
export function displayedFileSideWidths(input: {
  filesPref: number;
  markingPref: number;
  viewportWidth: number;
  files: FileSidePresence;
  marking: FileSidePresence;
}): { files: number; marking: number } {
  const markingOccupied = (width: number) => fileSideOccupied(input.marking, width);
  const filesOccupied = (width: number) => fileSideOccupied(input.files, width);
  let files =
    input.files === 'open'
      ? clampFileSideWidth(input.filesPref, input.viewportWidth, markingOccupied(input.markingPref))
      : input.filesPref;
  const marking =
    input.marking === 'open'
      ? clampFileSideWidth(input.markingPref, input.viewportWidth, filesOccupied(files))
      : input.markingPref;
  if (input.files === 'open') {
    files = clampFileSideWidth(input.filesPref, input.viewportWidth, markingOccupied(marking));
  }
  return { files, marking };
}

export function stackRailPositions(
  positions: { id: string; y: number }[],
): { id: string; y: number }[] {
  const sorted = [...positions].sort((a, b) => a.y - b.y || a.id.localeCompare(b.id));
  const stacked: { id: string; y: number }[] = [];
  for (const item of sorted) {
    const prev = stacked[stacked.length - 1];
    const y =
      prev && item.y < prev.y + HIGHLIGHT_RAIL_MIN_GAP ? prev.y + HIGHLIGHT_RAIL_MIN_GAP : item.y;
    stacked.push({ id: item.id, y });
  }
  return stacked;
}

export function quotePreview(quote: string): string {
  const trimmed = quote.replace(/\s+/g, ' ').trim();
  return trimmed ? `»${trimmed}«` : '';
}
